import React from 'react';
import styled from 'styled-components';
import { Body5, Body7, Container, TextField } from '@ui';
import { GhostButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { formatCount } from './format';
import { MAX_ENTRIES, removeEntry, summarizeResolution } from './resolveCompanies';

// The optional company drill-down filter. /feed/view takes numeric company_ids, so a pasted list of
// tickers has to be resolved before it can be sent — see resolveCompanies.js for why that is one
// request per symbol and why nothing is guessed.
//
// The textarea is the input; the chips below are the filter. Keeping the filter as resolved chips
// rather than as raw text is what lets buildViewParams, the collapsed summary and the run history
// stay exactly as they were, and it gives per-company removal back.
//
// Whole-corpus is deliberately not expressible on this endpoint — an empty filter means "no company
// filter", which is as wide as the view gets.

// TextField's textarea already resizes vertically and carries the border, radius and focus ring.
// The floor stays here: three rows is what a pasted column of tickers needs before it starts
// scrolling, and it is a fact about THIS field rather than about textareas.
const PasteField = styled.div`
  textarea {
    min-height: 72px;
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  padding: 3px 6px 3px 8px;
  font-size: 12px;
  background: ${({ theme, $unverified }) => ($unverified ? 'transparent' : theme.themeColors.primaryCtaButton)};
  border: 1px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
  color: ${({ theme, $unverified }) => ($unverified ? theme.themeColors.text : theme.themeColors.buttonText)};
`;

// A glyph rather than the shared Close svg: that component derives its height from a required
// `width` prop and paints from its own `color` prop, so dropping it into a two-tone chip means
// threading the theme in just to tint an 9px cross.
const ChipRemove = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  line-height: 1;

  /* Overrides the canonical ring on purpose. This sits inside a filled two-tone chip, where a 2px
     primary-coloured outline would fight the chip's own background; currentColor tracks whichever
     of the two tones the chip is wearing. */
  &:focus-visible {
    outline: 1px solid currentColor;
  }
`;

const UnresolvedRow = styled.div`
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  padding: 8px 0;
`;

const CandidateRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
`;

const REASONS = {
  'no-exact-match': 'no exact symbol match',
  'not-found': 'no company found',
  'lookup-failed': 'lookup failed',
};

const chipLabel = (company) => {
  if (company.symbol) return company.symbol;
  if (company.name) return company.name;
  return `#${company.company_id}`;
};

export const CompanyFilter = ({
  companies,
  onChange,
  text,
  onTextChange,
  onResolve,
  isResolving,
  progress,
  result,
  onDismissResult,
}) => {
  const removeCompany = (company) => onChange(companies.filter((c) => c !== company));

  const addCandidate = (candidate, entry) => {
    if (companies.some((c) => Number(c.company_id) === Number(candidate.company_id))) return;
    onChange([...companies, { company_id: candidate.company_id, symbol: candidate.symbol, name: candidate.name }]);
    // The token it came from leaves the textarea, since it is handled now. Through removeEntry
    // rather than a local split, so taking a token out agrees with how it was read in.
    onTextChange(removeEntry(text, entry));
    onDismissResult(entry);
  };

  const unresolved = (result && result.unresolved) || [];
  const hasUnverified = companies.some((c) => c.unverified);

  return (
    <div>
      <PasteField>
        <TextField
          multiline
          rows={3}
          aria-label="Companies"
          placeholder="AAPL, MSFT, 1234"
          value={text}
          onChange={onTextChange}
        />
      </PasteField>
      <Container top={8}>
        <Body7 isLowContrast>
          {`Tickers or company ids, separated by commas, spaces or new lines. Up to ${MAX_ENTRIES} at a time.`}
        </Body7>
      </Container>

      <Container top={8}>
        <SkeletonButton onClick={onResolve} buttonProps={{ 'aria-disabled': isResolving || !text.trim() || undefined }}>
          {isResolving ? `Adding… ${progress.done}/${progress.total}` : 'Add companies'}
        </SkeletonButton>
      </Container>

      {result && (
        <Container top={8}>
          <Body7 isLowContrast role="status">
            {summarizeResolution(result)}
          </Body7>
        </Container>
      )}

      {unresolved.length > 0 && (
        <Container top={8}>
          {unresolved.map((item) => (
            <UnresolvedRow key={item.entry}>
              <Body5>{`${item.entry} — ${REASONS[item.reason] || 'unresolved'}`}</Body5>
              {item.candidates.length > 0 && (
                <>
                  <Container top={4}>
                    <Body7 isLowContrast>Did you mean:</Body7>
                  </Container>
                  <CandidateRow>
                    {/* These came back on the search we already made, so offering them costs
                        nothing — and it is where search-by-name survives now that the typeahead
                        is gone. */}
                    {item.candidates.map((candidate) => (
                      <TransparentFlatButton
                        key={candidate.company_id}
                        onClick={() => addCandidate(candidate, item.entry)}
                      >
                        {candidate.symbol ? `${candidate.symbol} · ${candidate.name}` : candidate.name}
                      </TransparentFlatButton>
                    ))}
                  </CandidateRow>
                </>
              )}
            </UnresolvedRow>
          ))}
        </Container>
      )}

      {companies.length > 0 && (
        <Container top={16}>
          <ChipRow>
            {companies.map((company) => (
              <Chip key={company.company_id} $unverified={company.unverified}>
                {chipLabel(company)}
                <ChipRemove
                  type="button"
                  aria-label={`Remove ${chipLabel(company)}`}
                  onClick={() => removeCompany(company)}
                >
                  <span aria-hidden="true">×</span>
                </ChipRemove>
              </Chip>
            ))}
          </ChipRow>
          <Container top={8}>
            <Body7 isLowContrast>
              {`Filtering to ${formatCount(companies.length)} compan${companies.length === 1 ? 'y' : 'ies'}.`}
            </Body7>
          </Container>
          {hasUnverified && (
            <Container top={4}>
              <Body7 isLowContrast>
                Outlined chips are ids that were entered directly — nothing here can check them against a company, so a
                mistyped one just returns no rows.
              </Body7>
            </Container>
          )}
        </Container>
      )}
    </div>
  );
};
